/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @repo/internal/react/require-jsdoc */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { css as customCss, jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { isInsideContainer, useMouseTracking } from '../hooks/use-mouse-tracking';

const HIDDEN_POSITION = '-999px';
const VISIBILITY_THRESHOLD_PERCENT = 0.1;
const HOVER_RADIUS = '150px';

/**
 * Update mouse CSS variables on an element
 */
const setMousePosition = (element: HTMLDivElement, x: string, y: string) => {
	element.style.setProperty('--mouse-x', x);
	element.style.setProperty('--mouse-y', y);
};

/**
 * Hide the hover effect by moving it off-screen
 */
const hideHoverEffect = (element: HTMLDivElement) => {
	setMousePosition(element, HIDDEN_POSITION, HIDDEN_POSITION);
};

/**
 * Check if hero is scrolled mostly out of view (bottom above top 10% of viewport)
 */
const isHeroScrolledOut = (containerBottom: number, viewportHeight: number) => {
	const threshold = viewportHeight * VISIBILITY_THRESHOLD_PERCENT;
	return containerBottom < threshold;
};

const gridBackgroundStyles = cssMap({
	base: {
		position: 'absolute',
		inset: 0,
		// Base layer: 50% opacity dots using token with opacity
		// Solid dots: color stays constant from 0% to 1.7px (85% of radius), then fades to transparent
		backgroundImage: `radial-gradient(circle 2px at 2px 2px, ${token('color.border')} 0%, ${token('color.border')} 1.7px, transparent 2px)`,
		backgroundSize: '28px 28px', // 4px dot + 24px spacing = 28px repeat
		backgroundPosition: 'var(--grid-offset-x, 0px) var(--grid-offset-y, 0px)',
		pointerEvents: 'none',
	},
	hover: {
		backgroundImage: `radial-gradient(circle 2px at 2px 2px, ${token('color.border.bold')} 0%, ${token('color.border.bold')} 1.7px, transparent 2px)`,
		maskImage: `radial-gradient(circle ${HOVER_RADIUS} at var(--mouse-x, ${HIDDEN_POSITION}) var(--mouse-y, ${HIDDEN_POSITION}), var(--mask-color-full, white) 0%, var(--mask-color-half, rgba(255, 255, 255, 0.5)) 50%, transparent 100%)`,
		maskMode: 'alpha',
		transition: 'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
	},
});

const gridOverlayOpacity = customCss({
	opacity: 0.5,
});

const wrapperStyles = cssMap({
	base: {
		position: 'absolute',
		insetBlockStart: 0,
		insetInlineEnd: 0,
		insetBlockEnd: 0,
		insetInlineStart: 0,
		zIndex: -1,
		pointerEvents: 'none',
	},
});

interface HeroGridBackgroundProps {
	/**
	 * Optional ref to an element that the grid should align with.
	 * The grid will align its top-left corner with the top-left corner of this element.
	 */
	alignElementRef?: React.RefObject<HTMLElement>;
	/**
	 * Optional ref to the container element for mouse tracking.
	 * If not provided, the component will use its parent element.
	 */
	containerRef?: React.RefObject<HTMLElement>;
}

/**
 * __Hero Grid Background__
 *
 * A reusable grid background pattern component for hero sections.
 * Displays a dot grid pattern that adapts to the current color mode.
 * Includes interactive hover effects that follow the mouse cursor.
 */
export const HeroGridBackground: ({
	alignElementRef,
	containerRef,
}: HeroGridBackgroundProps) => JSX.Element = ({
	alignElementRef,
	containerRef,
}: HeroGridBackgroundProps) => {
	const hoverOverlayRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [gridOffset, setGridOffset] = useState({ x: 0, y: 0 });

	// Handle mouse position updates for hover effect
	const handlePositionUpdate = useCallback(
		(
			position: { x: number; y: number },
			rect: { bottom: number; width: number; height: number },
		) => {
			const overlay = hoverOverlayRef.current;
			if (!overlay) {
				return;
			}

			// Hide if hero is mostly scrolled out of view
			if (isHeroScrolledOut(rect.bottom, window.innerHeight)) {
				hideHoverEffect(overlay);
				return;
			}

			// Show hover effect only when mouse is inside container
			if (isInsideContainer(position.x, position.y, rect.width, rect.height)) {
				setMousePosition(overlay, `${position.x}px`, `${position.y}px`);
			} else {
				hideHoverEffect(overlay);
			}
		},
		[],
	);

	const handleMouseLeave = useCallback(() => {
		if (hoverOverlayRef.current) {
			hideHoverEffect(hoverOverlayRef.current);
		}
	}, []);

	// Use shared mouse tracking hook
	const { isReducedMotion, canHover } = useMouseTracking({
		containerRef,
		wrapperRef,
		callbacks: {
			onPositionUpdate: handlePositionUpdate,
			onMouseLeave: handleMouseLeave,
		},
	});

	// Calculate grid offset to align with the top-left of the align element
	useEffect(() => {
		if (typeof window === 'undefined' || !alignElementRef?.current || !wrapperRef.current) {
			return;
		}

		const container = containerRef?.current || wrapperRef.current.parentElement;
		if (!container) {
			return;
		}

		const updateGridOffset = () => {
			if (!alignElementRef?.current || !container) {
				return;
			}

			const containerRect = container.getBoundingClientRect();
			const alignElementRect = alignElementRef.current.getBoundingClientRect();

			// Calculate the top-left position of the align element relative to the container
			const offsetX = alignElementRect.left - containerRect.left;
			const offsetY = alignElementRect.top - containerRect.top;

			setGridOffset({ x: offsetX, y: offsetY });
		};

		updateGridOffset();

		const unbindResize = bind(window, {
			type: 'resize',
			listener: updateGridOffset,
		});

		return () => {
			unbindResize();
		};
	}, [alignElementRef, containerRef]);

	return (
		<div aria-hidden="true" ref={wrapperRef} css={wrapperStyles.base}>
			<div
				css={[gridBackgroundStyles.base, gridOverlayOpacity]}
				style={
					{
						'--grid-offset-x': `${gridOffset.x}px`,
						'--grid-offset-y': `${gridOffset.y}px`,
					} as React.CSSProperties
				}
			/>
			{canHover && !isReducedMotion && (
				<div
					css={[gridBackgroundStyles.base, gridBackgroundStyles.hover, gridOverlayOpacity]}
					ref={hoverOverlayRef}
					style={
						{
							'--grid-offset-x': `${gridOffset.x}px`,
							'--grid-offset-y': `${gridOffset.y}px`,
						} as React.CSSProperties
					}
				/>
			)}
		</div>
	);
};
